// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

export type EnsureCurrentPlayerOptions = {
  timeoutMs?: number;
  intervalMs?: number;
  signal?: AbortSignal;
  /**
   * Keep polling while the lookup 404s, until the timeout.
   *
   * Off by default: for callers that run on render (the header, the profile screen) a missing
   * player means it is missing for this session, and retrying only hammers the endpoint.
   *
   * Right after registration it is the opposite — the player record is created asynchronously
   * from a CreatePlayerCommand, so a 404 is the expected transient state and giving up on the
   * first one is what makes the very first "Create tour" fail.
   */
  retryWhileMissing?: boolean;
};

export type EnsureCurrentPlayerResult =
  | { status: 'resolved'; playerId: string }
  | { status: 'notFound' }
  | { status: 'timeout' }
  | { status: 'unauthorized' }
  | { status: 'error'; message: string };

const DEFAULT_TIMEOUT_MS = 10_000;
const DEFAULT_INTERVAL_MS = 400;

const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);

    const onAbort = () => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };

    const cleanup = () => {
      clearTimeout(timer);
      if (signal) {
        signal.removeEventListener('abort', onAbort);
      }
    };

    if (signal) {
      if (signal.aborted) {
        cleanup();
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
    }
  });

export async function ensureCurrentPlayer(
  apiBaseUrl: string,
  token: string,
  userId: string,
  options?: EnsureCurrentPlayerOptions
): Promise<EnsureCurrentPlayerResult> {
  const timeoutMs = Math.max(1, options?.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  const intervalMs = Math.max(1, options?.intervalMs ?? DEFAULT_INTERVAL_MS);
  const retryWhileMissing = options?.retryWhileMissing ?? false;
  const signal = options?.signal;
  const startedAt = Date.now();
  let sawMissing = false;

  while (Date.now() - startedAt <= timeoutMs) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    try {
      const response = await fetch(`${apiBaseUrl}/api/player/by-user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });

      if (response.ok) {
        const payload = (await response.json()) as { id?: string };
        if (payload.id) {
          return { status: 'resolved', playerId: payload.id };
        }
        return { status: 'error', message: 'Missing player id in response.' };
      }

      if (response.status === 401 || response.status === 403) {
        return { status: 'unauthorized' };
      }

      if (response.status !== 404) {
        return { status: 'error', message: `Failed to load player (${response.status})` };
      }

      if (!retryWhileMissing) {
        // Avoid hammering by-user lookups that stay unresolved for this session.
        return { status: 'notFound' };
      }

      // Fall through to the sleep below and look again — the record is probably still in flight.
      sawMissing = true;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      return { status: 'error', message: error instanceof Error ? error.message : String(error) };
    }

    if (Date.now() - startedAt > timeoutMs) {
      break;
    }

    await sleep(intervalMs, signal);
  }

  // Distinguish "we polled and it never showed up" from a transport give-up, so callers can
  // keep telling the user the same thing they did before this became retryable.
  return sawMissing ? { status: 'notFound' } : { status: 'timeout' };
}
